import React, { useEffect, useState } from 'react'
import { Icon } from '../components/gp'
import { UserSettingsApi } from '../apis/UserSettingsApi'

function ReloadRow({ icon, label, sublabel, value, editable, onEdit, badge, valueColor, iconAccent }) {
    const accent = editable || iconAccent
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '14px 18px',
                borderBottom: '1px solid var(--gp-hairline)',
            }}
        >
            <div
                style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: accent ? 'var(--gp-lime-soft)' : 'var(--gp-surface)',
                    color: accent ? 'var(--gp-brand)' : 'var(--gp-ink-mute)',
                    border: accent ? 'none' : '1px solid var(--gp-hairline)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Icon name={icon} size={accent ? 18 : 16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>{label}</span>
                    {badge && (
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 10px',
                                borderRadius: 999,
                                background: editable ? 'var(--gp-lime-soft)' : 'var(--gp-surface)',
                                color: editable ? 'var(--gp-brand)' : 'var(--gp-ink-mute)',
                                border: editable ? 'none' : '1px solid var(--gp-hairline)',
                                font: 'var(--gp-micro)',
                                letterSpacing: 0.4,
                            }}
                        >
                            {badge}
                        </span>
                    )}
                </div>
                {sublabel && (
                    <div style={{ font: 'var(--gp-caption)', color: 'var(--gp-ink-mute)', marginTop: 2 }}>
                        {sublabel}
                    </div>
                )}
            </div>
            {value != null && (
                <div
                    style={{
                        font: '500 18px/1 var(--gp-font)',
                        color: valueColor || 'var(--gp-ink)',
                        letterSpacing: -0.2,
                        textAlign: 'right',
                        marginRight: editable ? 4 : 0,
                    }}
                >
                    {value}
                </div>
            )}
            {editable && (
                <button
                    type="button"
                    onClick={onEdit}
                    style={{
                        height: 36,
                        padding: '0 16px',
                        borderRadius: 999,
                        border: '1px solid var(--gp-brand)',
                        background: 'transparent',
                        color: 'var(--gp-brand)',
                        font: '500 13px/1 var(--gp-font)',
                        cursor: 'pointer',
                        transition: 'background 0.15s ease',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--gp-lime-soft)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                    Edit
                </button>
            )}
        </div>
    )
}

function ReloadSettingsPanel({ currentDollars, milesPerWeek, defaultCardLast4, onSaved }) {
    const [editing, setEditing] = useState(false)
    const [amount, setAmount] = useState(currentDollars || 25)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const presets = [25, 50, 75, 100]

    // Re-sync the editor when the underlying value changes from elsewhere.
    useEffect(() => {
        if (!editing) setAmount(currentDollars || 25)
    }, [currentDollars, editing])

    const handleCancel = () => {
        setAmount(currentDollars || 25)
        setError('')
        setEditing(false)
    }

    const handleSave = () => {
        const dollars = Number(amount)
        if (isNaN(dollars) || dollars < 25 || dollars > 100) {
            setError('Amount has to be between $25 and $100.')
            return
        }
        setError('')
        setSaving(true)
        UserSettingsApi.put({
            milesPerWeek: milesPerWeek || 0,
            topUpAmount: dollars * 100,
            initialSetupComplete: true,
        })
            .then(() => {
                onSaved()
                setEditing(false)
            })
            .catch(() => setError('Could not update. Please try again.'))
            .finally(() => setSaving(false))
    }

    return (
        <div
            style={{
                marginTop: 18,
                background: 'var(--gp-card)',
                border: '1px solid var(--gp-hairline)',
                borderRadius: 'var(--gp-r-md)',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    padding: '14px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid var(--gp-hairline)',
                }}
            >
                <div>
                    <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>
                        Reload settings
                    </div>
                    <div style={{ font: 'var(--gp-caption)', color: 'var(--gp-ink-mute)', marginTop: 2 }}>
                        Charged to Primary card ending {defaultCardLast4 || '—'} when your balance falls below the threshold.
                    </div>
                </div>
            </div>

            <ReloadRow
                icon="wallet"
                label="Top up amount"
                sublabel="Added to your wallet on each reload"
                value={editing ? null : `$${currentDollars || 0}.00`}
                editable={!editing}
                onEdit={() => setEditing(true)}
                badge="Editable"
            />

            {editing && (
                <div
                    style={{
                        padding: '4px 18px 18px',
                        borderBottom: '1px solid var(--gp-hairline)',
                        background: 'var(--gp-surface)',
                    }}
                >
                    <div
                        style={{
                            font: 'var(--gp-micro)',
                            color: 'var(--gp-ink-mute)',
                            letterSpacing: 0.4,
                            textTransform: 'uppercase',
                            margin: '12px 0 10px',
                        }}
                    >
                        Choose a preset
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {presets.map((p) => {
                            const active = Number(amount) === p
                            return (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => setAmount(p)}
                                    style={{
                                        height: 40,
                                        minWidth: 72,
                                        padding: '0 16px',
                                        borderRadius: 10,
                                        border: '1.5px solid ' + (active ? 'var(--gp-brand)' : 'var(--gp-hairline)'),
                                        background: active ? 'var(--gp-brand)' : 'var(--gp-card)',
                                        color: active ? '#FFFFFF' : 'var(--gp-ink)',
                                        font: '500 14px/1 var(--gp-font)',
                                        cursor: 'pointer',
                                        transition: 'all 0.12s ease',
                                    }}
                                >
                                    ${p}
                                </button>
                            )
                        })}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                height: 40,
                                padding: '0 12px',
                                borderRadius: 10,
                                border: '1.5px solid var(--gp-hairline)',
                                background: 'var(--gp-card)',
                            }}
                        >
                            <span style={{ font: '500 14px/1 var(--gp-font)', color: 'var(--gp-ink-mute)' }}>$</span>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(Math.max(0, +e.target.value || 0))}
                                style={{
                                    width: 64,
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    font: '500 14px/1 var(--gp-mono)',
                                    color: 'var(--gp-ink)',
                                }}
                            />
                        </div>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={saving}
                                style={{
                                    height: 40,
                                    padding: '0 16px',
                                    borderRadius: 999,
                                    border: '1px solid var(--gp-brand)',
                                    background: 'transparent',
                                    color: 'var(--gp-brand)',
                                    font: '500 13px/1 var(--gp-font)',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    opacity: saving ? 0.5 : 1,
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={saving}
                                style={{
                                    height: 40,
                                    padding: '0 18px',
                                    borderRadius: 999,
                                    border: 'none',
                                    background: 'var(--gp-brand)',
                                    color: '#FFFFFF',
                                    font: '500 13px/1 var(--gp-font)',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    opacity: saving ? 0.6 : 1,
                                }}
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                    {error && (
                        <div
                            style={{
                                font: 'var(--gp-caption)',
                                color: 'var(--gp-danger)',
                                marginTop: 10,
                            }}
                        >
                            {error}
                        </div>
                    )}
                </div>
            )}

            <ReloadRow
                icon="bolt"
                label="Trigger threshold"
                sublabel="We reload when your balance drops below this amount"
                value="$10.00"
                editable={false}
                iconAccent
                badge="View only"
            />
        </div>
    )
}

export default ReloadSettingsPanel
